// components/MaintenanceGate.tsx
//
// Maintenance mode. Watches config/maintenance (admin-toggled from the Admin
// Dashboard) and, while enabled, covers the ENTIRE app with a maintenance
// screen for everyone who isn't an admin — players, staff, guests and
// signed-out visitors alike.
//
//   - Admins keep full access and instead see a slim persistent banner
//     reminding them the flag is still ON (so it doesn't get forgotten).
//   - Signed-out visitors get a "Staff sign-in" escape hatch that reveals the
//     login page for this browser session — that's how an admin gets in to
//     turn the flag off. Non-admins who sign in through it are re-blocked the
//     moment their account resolves.
//
// This is a UX gate, not a security boundary — the database rules still
// decide who can read/write what. Its job is to keep well-behaved clients
// away while data is being migrated or a deploy is in flight.

import { ReactElement, useEffect, useState } from 'react';
import { Box, Typography, Button, alpha } from '@mui/material';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { ref, onValue } from 'firebase/database';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';

const BYPASS_KEY = 'rm_maintenance_login_bypass';

interface MaintenanceConfig {
  enabled?: boolean;
  message?: string;
}

export function MaintenanceGate(): ReactElement | null {
  const { user, loading } = useAuth();
  const [config, setConfig] = useState<MaintenanceConfig | null>(null);
  const [bypass, setBypass] = useState<boolean>(() => sessionStorage.getItem(BYPASS_KEY) === '1');

  useEffect(() => {
    const unsub = onValue(
      ref(db, 'config/maintenance'),
      (snap) => setConfig((snap.val() as MaintenanceConfig | null) || null),
      () => setConfig(null),
    );
    return () => unsub();
  }, []);

  const enabled = config?.enabled === true;
  const isAdmin = user?.accountType === 'admin' || user?.role === 'admin';

  // A non-admin signing in through the escape hatch loses the bypass — the
  // hatch only exists so an ADMIN can reach the login form.
  useEffect(() => {
    if (bypass && user && !isAdmin) {
      sessionStorage.removeItem(BYPASS_KEY);
      setBypass(false);
    }
  }, [bypass, user, isAdmin]);

  if (!enabled) return null;
  // Don't flash the overlay at an admin while their account record loads.
  if (loading) return null;

  // Admins: slim always-visible reminder instead of the block.
  if (isAdmin) {
    return (
      <Box
        sx={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 14000,
          py: 0.5, textAlign: 'center',
          backgroundColor: '#FF6B35', color: '#0A0E14',
          fontSize: '0.78rem', fontWeight: 700, letterSpacing: 0.3,
        }}
      >
        🔧 MAINTENANCE MODE IS ON — only admins can use the app. Turn it off from the Admin Dashboard when you're done.
      </Box>
    );
  }

  // Signed-out visitor who tapped "Staff sign-in": let the login page show.
  if (!user && bypass) return null;

  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: 14000,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        px: 3, textAlign: 'center',
        background: 'radial-gradient(circle at 50% 30%, #141A24 0%, #0A0E14 70%)',
      }}
    >
      <Box
        sx={{
          width: 96, height: 96, borderRadius: '50%', mb: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: alpha('#FF6B35', 0.15), border: `2px solid ${alpha('#FF6B35', 0.5)}`,
          animation: 'rmMaintPulse 2.5s ease-in-out infinite',
          '@keyframes rmMaintPulse': {
            '0%, 100%': { boxShadow: `0 0 0 0 ${alpha('#FF6B35', 0.35)}` },
            '50%': { boxShadow: `0 0 0 18px ${alpha('#FF6B35', 0)}` },
          },
        }}
      >
        <EngineeringIcon sx={{ fontSize: 48, color: '#FF6B35' }} />
      </Box>

      <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', sm: '1.8rem' }, color: '#FFFFFF', mb: 1 }}>
        We&apos;ll be right back
      </Typography>
      <Typography sx={{ color: alpha('#FFFFFF', 0.7), maxWidth: 460, mb: 1 }}>
        {config?.message?.trim() ||
          'RoundMaster is undergoing scheduled maintenance. Hang tight — we’ll be back shortly.'}
      </Typography>
      <Typography sx={{ color: alpha('#FFFFFF', 0.4), fontSize: '0.8rem', mb: 4 }}>
        Your data is safe. This page will start working again automatically once maintenance ends.
      </Typography>

      {!user && (
        <Button
          size="small"
          onClick={() => { sessionStorage.setItem(BYPASS_KEY, '1'); setBypass(true); }}
          sx={{ color: alpha('#FFFFFF', 0.45), textTransform: 'none', fontSize: '0.75rem' }}
        >
          Staff sign-in
        </Button>
      )}
    </Box>
  );
}

export default MaintenanceGate;
